import React from 'react';
import { motion } from 'framer-motion';

interface CardProps {
  children: React.ReactNode;
  className?: string;
  hover?: boolean;
  onClick?: () => void;
}

export default function Card({ children, className = '', hover = true, onClick }: CardProps): React.JSX.Element {
  return (
    <motion.div
      whileHover={hover ? { y: -4, scale: 1.01 } : undefined}
      whileTap={onClick ? { scale: 0.98 } : undefined}
      transition={{ duration: 0.2 }}
      onClick={onClick}
      className={`
        bg-white rounded-2xl border border-slate-100
        shadow-sm hover:shadow-lg transition-shadow duration-300
        overflow-hidden
        ${onClick ? 'cursor-pointer' : ''}
        ${className}
      `}>
      {children}
    </motion.div>
  );
}

export function CardImage({ src, alt, className = '' }: { src?: string; alt?: string; className?: string }): React.JSX.Element {
  if (!src) {
    return <div className={`bg-slate-100 ${className}`} />;
  }
  return (
    <div className={`overflow-hidden ${className}`}>
      <img
        src={src}
        alt={alt || ''}
        className="w-full h-full object-cover transition-transform duration-500 hover:scale-105"
        loading="lazy"
      />
    </div>
  );
}

export function CardContent({ children, className = '' }: { children: React.ReactNode; className?: string }): React.JSX.Element {
  return <div className={`p-5 ${className}`}>{children}</div>;
}

export function CardBadge({ children, color = 'blue' }: { children: React.ReactNode; color?: 'blue' | 'green' | 'amber' | 'purple' | 'red' }): React.JSX.Element {
  const colors = {
    blue: 'bg-amber-50 text-amber-600',
    green: 'bg-green-50 text-green-700',
    amber: 'bg-amber-50 text-amber-700',
    purple: 'bg-purple-50 text-purple-700',
    red: 'bg-red-50 text-red-700',
  };
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold ${colors[color]}`}>
      {children}
    </span>
  );
}
